import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { Grid, Box } from '@material-ui/core';
import { BASE_URL } from '../../routes';
import Loader from '../../components/Loader';
import TabPanel from '../../components/TabPanel';
import ViolationData from './Tabs/ViolationData';
import BreadCrumb from '../../components/BreadCrumb';
import Title from '../../components/PageHeader/Title';
import ViolationComment from './Tabs/ViolationComment';
import Header from '../../components/PageHeader/Header';
import { getPersonSimpleName } from '../../utils/utils';
import PageNotFound from '../../components/PageNotFound';
import { Person } from '../../store/modules/person/types';
import { VIOLATION_DETAIL_TAB_LIST } from '../../store/app.constants';
import DetailPageComponent from '../../components/DetailPageComponent';
import { BreadCrumbs, Form, Violation } from '../../store/modules/application/types';

type ViolationDetailTabsProps = {
    match: { params: { id: string } };
    getViolationDetails: Function;
    isLoading: boolean;
    editForm: Form;
    violationDetail: Violation;
    setActiveTab: Function;
    getViolationCommentList: Function;
    ViolationCommentList: Comment;
    personDetail: Person;
    getApplication: Function;
    hasMoreViolationComments: boolean;
};

const ViolationDetailTabs: FunctionComponent<ViolationDetailTabsProps> = (props) => {
    const {
        getViolationDetails,
        isLoading,
        editForm,
        violationDetail,
        setActiveTab,
        getViolationCommentList,
        personDetail,
        getApplication,
        hasMoreViolationComments,
    } = props;
    const [tabList, setTabList] = useState<{ id: number; name: string }[]>();
    const [breadCrumbs, setBreadCrumbs] = useState<BreadCrumbs>();
    const violationId = props.match.params.id;
    const licenseId = violationDetail.license?.id;
    const [skipRecord, setSkipRecord] = useState(0);
    const recordLimit = 20;

    const makeBreadCrumbs = useCallback(() => {
        if (violationDetail && violationDetail.license && !!personDetail) {
            const licenseNameAndNumber =
                violationDetail.license.type.name +
                `${violationDetail.license?.licenseNumber ? ` ${violationDetail.license?.licenseNumber}` : ''}`;

            const inspectionTypeAndDate = violationDetail?.inspection
                ? ` > ${violationDetail?.inspection?.type?.name} ${
                      violationDetail?.inspection?.inspectionDate
                          ? moment(violationDetail?.inspection?.inspectionDate).format('L')
                          : ''
                  } `
                : '';
            return [
                {
                    label: `${getPersonSimpleName(personDetail.name)}  > `,
                    path: BASE_URL + `/person/${personDetail && personDetail.id}/detail`,
                },
                {
                    label: licenseNameAndNumber,
                    path: BASE_URL + `/license/${violationDetail.license.id}/detail`,
                },
                {
                    label: inspectionTypeAndDate,
                    path: BASE_URL + `/inspection/${violationDetail?.inspection?.id}/detail`,
                },
            ];
        }
        return [];
    }, [violationDetail, personDetail]);

    useEffect(() => {
        getViolationDetails(violationId);
    }, [getViolationDetails, violationId]);

    useEffect(() => {
        getViolationCommentList(violationId, { limit: recordLimit, skip: skipRecord });
    }, [getViolationCommentList, violationId, skipRecord]);

    useEffect(() => {
        setTabList(VIOLATION_DETAIL_TAB_LIST);
    }, []);

    useEffect(() => {
        const response = makeBreadCrumbs();
        setBreadCrumbs(response);
    }, [makeBreadCrumbs]);

    useEffect(() => {
        if (!personDetail && !!licenseId) {
            getApplication(licenseId);
        }
    }, [personDetail, licenseId, getApplication]);

    const onTabSelect = (tabName: string) => {
        setActiveTab(tabName);
    };

    const renderTabsPanel = (activeTab: number) => {
        const tab = tabList && tabList[activeTab];
        switch (tab?.id) {
            case 0:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <ViolationData violationDetails={violationDetail} editForm={editForm} />
                    </TabPanel>
                );

            case 1:
                return (
                    <TabPanel activeTab={activeTab} index={activeTab}>
                        <ViolationComment
                            violationDetails={violationDetail}
                            skip={skipRecord}
                            setSkip={setSkipRecord}
                            hasMore={hasMoreViolationComments}
                        />
                    </TabPanel>
                );

            default:
                return <h1>Something went wrong. We are on it.</h1>;
        }
    };

    if (isLoading) return <Loader />;
    else if (!isLoading && !violationDetail.id) return <PageNotFound />;
    return (
        <div className="verticalFillContainer">
            <Header>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                    <BreadCrumb breadCrumbs={breadCrumbs!} />
                    <Title titleText={violationDetail?.category?.name} />
                </Grid>
            </Header>

            <Box width="100%" display="flex" className="detail detail-mobile verticalFillContent">
                <DetailPageComponent
                    displayTabs={renderTabsPanel}
                    tabList={tabList}
                    onTabSelect={onTabSelect}
                ></DetailPageComponent>
            </Box>
        </div>
    );
};

export default ViolationDetailTabs;
